import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import FormalRVHeader from '../components/formalrv/FormalRVHeader'
import FrontierChart, { ChartLegend } from '../components/formalrv/FrontierChart'
import { ChainDots, ChainLegend, LevelBadge, Tag } from '../components/formalrv/badges'
import { fmtDuration, fmtMode, fmtQubits, fmtRatio, isDefaultMode } from '../components/formalrv/format'
import { formalrvApi, type FRVEntrySummary, type FRVLeaderboard, type FRVScreened } from '../utils/api'

const LEVEL_ORDER = { V3: 0, V2: 1, V1: 2, V0: 3 }

function ClaimCell({ e }: { e: FRVEntrySummary }) {
  if (!e.claim) return <span className="text-qcloud-muted text-xs">Control study</span>
  return (
    <div>
      <div className="font-semibold text-qcloud-text tabular-nums">
        {e.claim.physical_qubits_qualifier === 'under' && '< '}{fmtQubits(e.claim.physical_qubits)} · {fmtDuration(e.claim.runtime_days)}
      </div>
      <div className="text-[11px] text-qcloud-muted">{e.claim.runtime_basis}</div>
    </div>
  )
}

function CheckedCell({ e }: { e: FRVEntrySummary }) {
  if (e.checked) {
    return (
      <div>
        <div className="font-semibold text-qcloud-text tabular-nums">
          {fmtQubits(e.checked.peak_qubits)} · {fmtDuration(e.checked.runtime_days)}
        </div>
        <div className="text-[11px] text-qcloud-muted">peak qubits · {e.checked.runtime_basis}</div>
      </div>
    )
  }
  const w = e.replay?.witnesses.find(w => w.physical_error === e.claim?.physical_error) ?? e.replay?.witnesses[0]
  if (w) {
    return (
      <div>
        <div className="font-semibold text-qcloud-text tabular-nums">{fmtQubits(w.physical_qubits)} · {fmtDuration(w.expected_days)}</div>
        <div className="text-[11px] text-qcloud-muted">equation replay, not a compiled schedule</div>
      </div>
    )
  }
  return <span className="text-[11px] text-qcloud-muted">No timed schedule yet</span>
}

function GapCell({ e }: { e: FRVEntrySummary }) {
  if (!e.claim || !e.checked) return <span className="text-qcloud-muted">—</span>
  return (
    <div className="text-xs tabular-nums leading-5">
      <div><span className="text-qcloud-muted">time </span><span className="font-semibold text-qcloud-text">{fmtRatio(e.checked.runtime_days, e.claim.runtime_days)}</span></div>
      <div><span className="text-qcloud-muted">qubits </span><span className="font-semibold text-qcloud-text">{fmtRatio(e.checked.peak_qubits, e.claim.physical_qubits)}</span></div>
    </div>
  )
}

function Row({ e, onOpen }: { e: FRVEntrySummary; onOpen: (id: string) => void }) {
  const c = e.checked
  return (
    <tr
      onClick={() => onOpen(e.id)}
      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
    >
      <td className="pl-5 pr-2 py-3 align-top text-lg font-bold text-qcloud-text tabular-nums">
        {e.rank ?? <span className="text-sm font-normal text-qcloud-muted">—</span>}
      </td>
      <td className="px-3 py-3 align-top">
        <Link to={`/shor/${e.id}`} onClick={ev => ev.stopPropagation()} className="font-semibold text-qcloud-text hover:text-qcloud-primary">
          {e.short_name}
        </Link>
        <div className="text-[11px] text-qcloud-muted leading-snug">{e.platform}</div>
      </td>
      <td className="px-3 py-3 align-top"><ClaimCell e={e} /></td>
      <td className="px-3 py-3 align-top"><CheckedCell e={e} /></td>
      <td className="px-3 py-3 align-top"><GapCell e={e} /></td>
      <td className="px-3 py-3 align-top">
        <LevelBadge level={e.level} />
        <div className="mt-1.5"><ChainDots chain={e.chain} /></div>
      </td>
      <td className="px-3 pr-5 py-3 align-top">
        <div className="flex flex-wrap gap-1">
          {e.flags?.map(f => <Tag key={f}>{f}</Tag>)}
          {c && !isDefaultMode(c.vm_mode, c.require_specs) && (
            <Tag title="The VM verdict was not produced in the default --require-specs mode">{fmtMode(c.vm_mode)} mode</Tag>
          )}
          {c?.verdict_archived === false && <Tag title="The verdict is recorded in the audit guide; the verdict file is not yet installed">verdict in guide only</Tag>}
          {c && c.machine_qubits && e.claim && c.machine_qubits > e.claim.physical_qubits && (
            <Tag title={`Checked on a machine of ${c.machine_qubits.toLocaleString()} qubits`}>machine enlarged</Tag>
          )}
        </div>
      </td>
    </tr>
  )
}

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <tr className="border-t border-qcloud-border bg-slate-50/70">
      <td colSpan={7} className="px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-qcloud-muted">{children}</td>
    </tr>
  )
}

function LeaderboardTable({ data }: { data: FRVLeaderboard }) {
  const navigate = useNavigate()
  const open = (id: string) => navigate(`/shor/${id}`)
  const ranked = data.entries.filter(e => e.rank !== null).sort((a, b) => a.rank! - b.rank!)
  const baselines = data.entries.filter(e => e.kind === 'baseline')
  const progress = data.entries
    .filter(e => e.rank === null && e.kind === 'paper')
    .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level])

  return (
    <section className="bg-white rounded-2xl border border-qcloud-border overflow-hidden">
      <div className="px-5 sm:px-6 py-4 border-b border-qcloud-border">
        <h2 className="text-xl font-bold text-qcloud-text">Leaderboard</h2>
        <p className="text-sm text-qcloud-muted mt-0.5">
          Ranked by checked spacetime volume (peak qubits × runtime per run). Only full schedules admitted by the resource VM are ranked.
          Click a row for the verification chain, trusted inputs and evidence.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-qcloud-muted text-left">
              <th className="pl-5 pr-2 py-2.5 w-10">#</th>
              <th className="px-3 py-2.5">Paper</th>
              <th className="px-3 py-2.5">Paper's claim</th>
              <th className="px-3 py-2.5">Checked by FormalRV</th>
              <th className="px-3 py-2.5">Checked / claimed</th>
              <th className="px-3 py-2.5">Verification</th>
              <th className="px-3 pr-5 py-2.5">Qualifications</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map(e => <Row key={e.id} e={e} onOpen={open} />)}
            {baselines.length > 0 && <GroupHeader>Reference</GroupHeader>}
            {baselines.map(e => <Row key={e.id} e={e} onOpen={open} />)}
            {progress.length > 0 && <GroupHeader>Audit in progress — not ranked</GroupHeader>}
            {progress.map(e => <Row key={e.id} e={e} onOpen={open} />)}
          </tbody>
        </table>
      </div>
      <div className="px-5 sm:px-6 py-3 border-t border-qcloud-border"><ChainLegend /></div>
    </section>
  )
}

function Levels({ data }: { data: FRVLeaderboard }) {
  return (
    <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {[...data.levels].reverse().map(l => (
        <div key={l.id} className="bg-white rounded-2xl border border-qcloud-border p-4">
          <LevelBadge level={l.id} />
          <p className="text-sm text-qcloud-text mt-2 leading-snug">{l.meaning}</p>
        </div>
      ))}
    </section>
  )
}

function Screened({ rows }: { rows: FRVScreened[] }) {
  const [all, setAll] = useState(false)
  const sorted = [...rows].sort((a, b) => Number(b.recommended) - Number(a.recommended))
  const shown = all ? sorted : sorted.slice(0, 6)
  return (
    <section className="bg-white rounded-2xl border border-qcloud-border overflow-hidden">
      <div className="px-5 sm:px-6 py-4 border-b border-qcloud-border flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-qcloud-text">Screened, not yet audited</h2>
          <p className="text-sm text-qcloud-muted">
            Published Shor resource estimates that meet the eligibility criteria (V0). Effort class: C component replay · R reuse · E new scheduling · N new architecture · A new algorithm · X both.
          </p>
        </div>
        <span className="text-xs text-qcloud-muted">{rows.length} publications</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-qcloud-muted text-left">
              <th className="px-5 py-2.5">Study</th>
              <th className="px-3 py-2.5">Workload</th>
              <th className="px-3 py-2.5">Hardware model</th>
              <th className="px-3 pr-5 py-2.5">Effort</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.url} className="border-t border-slate-100 align-top">
                <td className="px-5 py-2.5">
                  <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-qcloud-text hover:text-qcloud-primary">{r.name}</a>
                  {r.recommended && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-qcloud-primary/10 text-qcloud-primary text-[10.5px] font-semibold">Recommended next</span>}
                  {r.component_scope && <span className="ml-2"><Tag>component scope</Tag></span>}
                </td>
                <td className="px-3 py-2.5 text-qcloud-text whitespace-nowrap">{r.workload}</td>
                <td className="px-3 py-2.5 text-qcloud-muted text-xs max-w-xs">{r.hardware}</td>
                <td className="px-3 pr-5 py-2.5 font-mono text-qcloud-text">{r.effort_class ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 6 && (
        <button onClick={() => setAll(!all)} className="w-full px-5 py-2.5 border-t border-qcloud-border text-sm font-medium text-qcloud-primary hover:bg-slate-50">
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </section>
  )
}

export default function ShorLeaderboardPage() {
  const [data, setData] = useState<FRVLeaderboard | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'FT Shor Leaderboard · QuantumArena'
    formalrvApi.getLeaderboard().then(setData).catch(err => setError(err?.message || 'Failed to load'))
  }, [])

  const checkedCount = data?.entries.filter(e => e.level === 'V3' && e.kind === 'paper').length ?? 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      <FormalRVHeader />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-qcloud-primary bg-qcloud-primary/10 rounded-full px-3 py-1 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-qcloud-primary" />
          Checked end-to-end by FormalRV
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-qcloud-text leading-tight">Fault-Tolerant Shor Leaderboard</h1>
        <p className="text-base md:text-lg text-qcloud-muted mt-3 max-w-3xl">
          How many physical qubits, and how much time, does factoring RSA-2048 take? Each published estimate is rebuilt as an
          explicit program, compiled through codes and a timed schedule on a declared machine, and checked by an independent resource VM.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/shor/learn" className="text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-qcloud-primary to-qcloud-secondary text-white hover:opacity-90">
            How is a schedule checked? Learn the stack →
          </Link>
          <Link to="/shor/explorer" className="text-sm font-semibold px-4 py-2 rounded-lg border border-qcloud-border bg-white text-qcloud-text hover:border-qcloud-primary">
            Open the Compiler Explorer
          </Link>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-800">
            <div className="font-semibold mb-1">Couldn't load the leaderboard</div>
            <div className="text-sm">{error}</div>
          </div>
        )}
        {!data && !error && (
          <div className="bg-white rounded-2xl border border-qcloud-border p-12 text-center text-qcloud-muted">Loading…</div>
        )}

        {data && (
          <>
            <section className="bg-white rounded-2xl border border-qcloud-border p-5 sm:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <h2 className="text-xl font-bold text-qcloud-text">Claimed vs checked</h2>
                <span className="text-xs text-qcloud-muted">{checkedCount} paper schedules checked · RSA-2048</span>
              </div>
              <p className="text-sm text-qcloud-muted mb-3">
                Each arrow runs from a paper's headline estimate to the figure FormalRV obtained for its compiled schedule. Lower-left is cheaper.
              </p>
              <div className="mb-2"><ChartLegend /></div>
              <FrontierChart entries={data.entries} />
              <p className="text-xs text-qcloud-muted mt-3 border-t border-slate-100 pt-3">{data.disclaimer}</p>
            </section>

            <LeaderboardTable data={data} />
            <Levels data={data} />
            {data.screened.length > 0 && <Screened rows={data.screened} />}

            <footer className="text-xs text-qcloud-muted text-center pt-2">
              Snapshot of FormalRV commit <span className="font-mono">{data.formalrv.commit.slice(0, 10)}</span>, generated {new Date(data.generated_at).toLocaleDateString()}.
              Every checked figure is read from a resource-VM verdict file; paper claims are quoted as cited by the audit guides.
            </footer>
          </>
        )}
      </main>
    </div>
  )
}
